import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { TranslocoCurrencyPipe } from '@jsverse/transloco-locale';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatBadgeModule } from '@angular/material/badge';
import { MatTabsModule } from '@angular/material/tabs';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatExpansionModule } from '@angular/material/expansion';
import { TableView, TypeOfColumn, TableColumnInfo } from 'design-system';
import { DynamicRouteService } from 'core';
import { ProductDetail, ProductDocument } from '../../interfaces/product-detail.model';
import { AvailabilityChip } from '../components/availability-chip/availability-chip';
import { AddToCartButton } from '../components/add-to-cart-button/add-to-cart-button';
import { ProductsService } from '../../services/products.service';
import { CartService } from '../../services/cart.service';
import { PRODUCTS_DESCRIBE } from '../constants';

interface BreadcrumbItem {
  label: string;
  link?: string;
}

@Component({
  selector: 'prd-products-detail',
  imports: [
    TranslocoModule,
    MatCardModule,
    MatButtonModule,
    MatBadgeModule,
    MatTabsModule,
    MatChipsModule,
    MatButtonToggleModule,
    MatExpansionModule,
    TableView,
    TranslocoCurrencyPipe,
  ],
  templateUrl: './products-detail.html',
  styleUrl: './products-detail.scss',
})
export class ProductsDetail implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly dynamicRouteService = inject(DynamicRouteService);
  private readonly productsService = inject(ProductsService);
  protected readonly cart = inject(CartService);

  productDetail: ProductDetail | null = null;
  documents: ProductDocument[] = [];
  documentTypeFilter = '';

  get filteredDocuments(): ProductDocument[] {
    if (!this.documentTypeFilter) {
      return this.documents;
    }
    return this.documents.filter(doc => doc.tipo === this.documentTypeFilter);
  }

  breadcrumbItems: BreadcrumbItem[] = [
    { label: 'Prodotti', link: '/products' },
  ];

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const productId = params.get('productId');

      if (!productId) {
        console.error('ID prodotto mancante in URL');
        return;
      }

      this.loadProduct(productId);
      this.loadDocuments(productId);
    });
  }

  openRelatedProduct(productId: string): void {
    let navigationArray = this.dynamicRouteService.getRoutePath(this.router, PRODUCTS_DESCRIBE, 'product-detail');
    if (navigationArray) {
      navigationArray = this.dynamicRouteService.replacePathSegments(navigationArray, [productId]);
      this.router.navigate(navigationArray, {});
    }
  }

  private loadProduct(productId: string): void {
    this.productsService.getProductDetail(productId).subscribe({
      next: (data: ProductDetail) => {
        this.productDetail = data;
        this.breadcrumbItems = [
          { label: 'Prodotti', link: '/products' },
          { label: data.plc1 },
          { label: data.plc2 },
          { label: data.nomeProdotto },
        ];
      },
      error: err => {
        console.error('Errore caricamento dettaglio prodotto', err);
      },
    });
  }

  private loadDocuments(productId: string): void {
    this.productsService.getProductDocuments(productId).subscribe({
      next: (data: ProductDocument[]) => {
        this.documents = data;
      },
      error: err => {
        console.error('Errore caricamento documenti prodotto', err);
      },
    });
  }

  downloadDocument(doc: ProductDocument): void {
    this.productsService.downloadProductDocument(doc.idAsset).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.nomeFile;
        a.click();
        URL.revokeObjectURL(url);
      },
      error: err => {
        console.error('Errore download documento', err);
      },
    });
  }

  get availableCodesTableData() {
    return (this.productDetail?.codiciDisponibili ?? []).map(p => ({
      ...p,
      'imballoDescrizione': `${p.nomeImballo} - ${p.unitaFatturabile} - ${p.unitaMisura}`,
    }));
  }

  get chemicalPropertiesTableData() {
    return (this.productDetail?.pimcore.proprietaChimiche ?? []).map(p => ({
      ...p,
      'metodo': p.metodo ?? '-',
      'unita': p.unita ?? '-',
    }));
  }

  chemicalPropertiesColumnsInfo: TableColumnInfo[] = [
    {
      columnNameDef: 'caratteristica',
      label: 'Caratteristica',
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'metodo',
      label: 'Metodo',
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'unita',
      label: 'Unità',
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'valore',
      label: 'Valore',
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
  ];

  specificationsColumnsInfo: TableColumnInfo[] = [
    {
      columnNameDef: 'sorgente',
      label: 'Sorgente',
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'sigla',
      label: 'Sigla',
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
  ];

  availableCodesColumnsInfo: TableColumnInfo[] = [
    {
      columnNameDef: 'sku',
      label: 'Cod. prodotto',
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'nomeProdotto',
      label: 'Nome prodotto',
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'imballoDescrizione',
      label: 'Imballo / Q.tà / UoM',
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'scala',
      label: 'Scala',
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'prezzo',
      label: 'Prezzo',
      type: TypeOfColumn.CURRENCY,
      sortable: false,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'plc1',
      label: 'PLC1',
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'plc2',
      label: 'PLC2',
      type: TypeOfColumn.TEXT,
      sortable: false,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'stock',
      label: 'Stock',
      type: TypeOfColumn.COMPONENT,
      sortable: false,
      component: AvailabilityChip,
      labelOnOneLine: true,
    },
    {
      columnNameDef: 'idProdotto',
      label: '',
      type: TypeOfColumn.COMPONENT,
      sortable: false,
      component: AddToCartButton,
    },
  ];
}
